// Giveaway weeks (TKT-0088). Reads WOB App giveaways from the theme Worker
// (GET /giveaways?campaign=), renders cards, revealed details, and entry forms,
// and posts entries to POST /giveaways/entries. The Worker resolves status, so
// this script only re-reads at the next schedule boundary (nextChangeAt).
(() => {
  if (customElements.get('giveaway-weeks')) return;

  const SHOP_TIME_ZONE = 'America/Chicago';
  const CARD_WIDTHS = [360, 540, 720, 960];
  const DETAIL_WIDTHS = [480, 720, 960, 1200];
  const MAX_REFRESH_DELAY = 6 * 60 * 60 * 1000;
  const STATUS_TEXT = {
    coming_soon: 'Coming soon',
    upcoming: 'Opening soon',
    open: 'Enter now',
    closed: 'Closed'
  };

  const formatTime = (value) => {
    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        timeZone: SHOP_TIME_ZONE, timeZoneName: 'short'
      }).format(date);
    } catch {
      return date.toLocaleString();
    }
  };

  // An absolute Worker base URL without a trailing slash. Parsing without a base
  // repairs a malformed "https:/host" instead of resolving it against the store.
  const endpointBase = (value) => {
    try {
      const url = new URL(String(value || '').trim());
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
    } catch {
      return '';
    }
  };

  const element = (tag, className = '', text = '') => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const sizedUrl = (url, width) => {
    try {
      const parsed = new URL(url);
      if (/(^|\.)shopify\.com$|shopifycdn\.net$/.test(parsed.hostname)) parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  };

  const picture = (image, className, widths, sizes, eager = false) => {
    const img = element('img', `${className} image-loading`);
    img.src = sizedUrl(image.url, widths[1]);
    img.srcset = widths.map((width) => `${sizedUrl(image.url, width)} ${width}w`).join(', ');
    img.sizes = sizes;
    img.alt = image.alt || '';
    img.decoding = 'async';
    img.loading = eager ? 'eager' : 'lazy';
    if (image.width && image.height) {
      img.width = image.width;
      img.height = image.height;
    }
    const loaded = () => {
      img.classList.remove('image-loading');
      img.classList.add('image-loaded');
    };
    if (img.complete && img.naturalWidth) loaded();
    else {
      img.addEventListener('load', loaded, { once: true });
      img.addEventListener('error', loaded, { once: true });
    }
    return img;
  };

  const paragraphs = (value, className) => {
    const wrapper = element('div', className);
    String(value || '').split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean).forEach((part) => {
      const paragraph = element('p');
      part.split('\n').forEach((line, index) => {
        if (index) paragraph.append(document.createElement('br'));
        paragraph.append(document.createTextNode(line));
      });
      wrapper.append(paragraph);
    });
    return wrapper.childElementCount ? wrapper : null;
  };

  const link = (href, className, text) => {
    const anchor = element('a', className, text);
    anchor.href = href;
    try {
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) {
        anchor.target = '_blank';
        anchor.rel = 'noopener';
        anchor.append(element('span', 'visually-hidden', ' (opens in a new tab)'));
      }
    } catch {
      // Relative storefront links stay in the same tab.
    }
    return anchor;
  };

  const statusLine = (giveaway) => {
    if (giveaway.status === 'coming_soon') {
      return giveaway.revealAt ? `Revealed ${formatTime(giveaway.revealAt)}` : STATUS_TEXT.coming_soon;
    }
    if (giveaway.status === 'upcoming') return giveaway.opensAt ? `Entries open ${formatTime(giveaway.opensAt)}` : STATUS_TEXT.upcoming;
    if (giveaway.status === 'open') return giveaway.closesAt ? `Enter by ${formatTime(giveaway.closesAt)}` : STATUS_TEXT.open;
    return STATUS_TEXT.closed;
  };

  class GiveawayWeeks extends HTMLElement {
    connectedCallback() {
      this.endpoint = endpointBase(this.dataset.endpoint);
      this.campaign = this.dataset.campaign || '';
      this.grid = this.querySelector('[data-giveaway-grid]');
      this.details = this.querySelector('[data-giveaway-details]');
      this.announcement = this.querySelector('[data-giveaway-announcement]');
      this.rendered = new Map();
      this.onVisibility = () => {
        if (!document.hidden && this.nextChangeAt && Date.now() >= this.nextChangeAt) this.load();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.load();
    }

    disconnectedCallback() {
      window.clearTimeout(this.refreshTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.controller?.abort();
    }

    async load() {
      window.clearTimeout(this.refreshTimer);
      this.controller?.abort();
      this.controller = new AbortController();
      if (!this.endpoint || !this.campaign) {
        this.showUnavailable('Set the campaign handle to show giveaways.');
        return;
      }
      try {
        const url = new URL(`${this.endpoint}/giveaways`);
        url.searchParams.set('campaign', this.campaign);
        const response = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !Array.isArray(data.giveaways)) {
          this.showUnavailable(response.status === 404
            ? `WOB App has not published a campaign with the handle "${this.campaign}".`
            : '');
          return;
        }
        this.data = data;
        this.render(data);
        this.scheduleRefresh(data.nextChangeAt);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showUnavailable('');
        // Retry once later in case the network blipped.
        this.refreshTimer = window.setTimeout(() => this.load(), 60 * 1000);
      }
    }

    scheduleRefresh(nextChangeAt) {
      const next = Date.parse(nextChangeAt || '');
      this.nextChangeAt = Number.isFinite(next) ? next : null;
      if (!this.nextChangeAt) return;
      // A little after the boundary, so the Worker's 15-second cache has turned over.
      const delay = this.nextChangeAt - Date.now() + 16000 + Math.random() * 4000;
      if (delay <= MAX_REFRESH_DELAY) this.refreshTimer = window.setTimeout(() => this.load(), Math.max(1000, delay));
    }

    showUnavailable(editorMessage) {
      this.grid?.querySelectorAll('[data-giveaway-skeleton]').forEach((node) => node.remove());
      this.grid?.removeAttribute('aria-busy');
      if (this.rendered.size) return;
      const unavailable = this.querySelector('[data-giveaway-unavailable]');
      if (unavailable) unavailable.hidden = false;
      const note = this.querySelector('[data-giveaway-editor-note]');
      if (note && editorMessage && this.dataset.designMode === 'true') note.textContent = editorMessage;
    }

    render(data) {
      const unavailable = this.querySelector('[data-giveaway-unavailable]');
      if (unavailable) unavailable.hidden = true;
      const campaign = data.campaign || {};
      const changed = [];
      const seen = new Set();
      const cards = [];
      const panels = [];

      data.giveaways.forEach((giveaway, index) => {
        const signature = JSON.stringify(giveaway);
        const previous = this.rendered.get(giveaway.id);
        seen.add(giveaway.id);
        if (previous && previous.signature === signature) {
          cards.push(previous.card);
          if (previous.panel) panels.push(previous.panel);
          return;
        }
        // Keep a shopper's half-filled form when only unrelated data changed.
        if (previous?.panel?.contains(document.activeElement) && previous.status === giveaway.status) {
          cards.push(previous.card);
          panels.push(previous.panel);
          return;
        }
        if (previous && previous.status !== giveaway.status) changed.push(giveaway);
        const card = this.card(giveaway, index);
        const panel = giveaway.status === 'coming_soon' ? null : this.panel(giveaway, campaign);
        this.rendered.set(giveaway.id, { signature, status: giveaway.status, card, panel });
        cards.push(card);
        if (panel) panels.push(panel);
      });
      [...this.rendered.keys()].forEach((id) => { if (!seen.has(id)) this.rendered.delete(id); });

      this.grid.replaceChildren(...cards);
      this.grid.removeAttribute('aria-busy');
      this.grid.dataset.count = String(cards.length);
      this.style.setProperty('--giveaway-columns', String(Math.min(4, Math.max(1, cards.length))));
      this.details.replaceChildren(...panels);

      if (changed.length && this.announcement) {
        this.announcement.textContent = changed
          .map((giveaway) => `${giveaway.label}: ${giveaway.status === 'coming_soon' ? STATUS_TEXT.coming_soon : `${giveaway.title}, ${STATUS_TEXT[giveaway.status].toLowerCase()}`}`)
          .join('. ');
      }
    }

    panelId(giveaway) {
      return `Giveaway-${this.closest('.shopify-section')?.id || 'section'}-${giveaway.id}`;
    }

    card(giveaway, index) {
      const card = element('article', `giveaway-card giveaway-card--${giveaway.status}`);
      card.dataset.giveawayId = giveaway.id;
      card.dataset.giveawayStatus = giveaway.status;
      const titleId = `${this.panelId(giveaway)}-card-title`;
      card.setAttribute('aria-labelledby', titleId);

      const header = element('div', 'giveaway-card__header');
      header.append(element('p', 'giveaway-card__label', giveaway.label));
      header.append(element('span', 'giveaway-card__rule'));
      const title = element('h3', 'giveaway-card__title', giveaway.title || STATUS_TEXT.coming_soon);
      title.id = titleId;
      header.append(title);
      card.append(header);

      const media = element('div', 'giveaway-card__media');
      if (giveaway.image?.url) {
        media.append(picture(giveaway.image, 'giveaway-card__image', CARD_WIDTHS, '(min-width: 990px) 24vw, (min-width: 750px) 45vw, 50vw', index < 4));
      }
      const footer = element('div', 'giveaway-card__footer');
      footer.append(element('p', `giveaway-card__status giveaway-card__status--${giveaway.status}`, statusLine(giveaway)));
      if (giveaway.status !== 'coming_soon') {
        const label = giveaway.status === 'open' && giveaway.entryMethod !== 'none'
          ? (giveaway.ctaLabel || 'Enter the giveaway')
          : 'See the details';
        const cta = link(`#${this.panelId(giveaway)}`, 'giveaway-card__cta', label);
        cta.setAttribute('aria-describedby', titleId);
        footer.append(cta);
      }
      media.append(footer);
      card.append(media);
      return card;
    }

    panel(giveaway, campaign) {
      const panel = element('section', `giveaway-detail giveaway-detail--${giveaway.status}`);
      panel.id = this.panelId(giveaway);
      panel.tabIndex = -1;
      const headingId = `${panel.id}-heading`;
      panel.setAttribute('aria-labelledby', headingId);

      if (giveaway.image?.url) {
        const media = element('div', 'giveaway-detail__media');
        media.append(picture(giveaway.image, 'giveaway-detail__image', DETAIL_WIDTHS, '(min-width: 990px) 40vw, 100vw'));
        panel.append(media);
      }

      const body = element('div', 'giveaway-detail__body');
      const meta = element('p', 'giveaway-detail__meta');
      meta.append(element('span', '', giveaway.label));
      meta.append(element('span', `giveaway-detail__status giveaway-detail__status--${giveaway.status}`, STATUS_TEXT[giveaway.status]));
      body.append(meta);
      const heading = element('h3', 'giveaway-detail__heading', giveaway.title);
      heading.id = headingId;
      body.append(heading);
      if (giveaway.summary) body.append(element('p', 'giveaway-detail__summary', giveaway.summary));
      const details = paragraphs(giveaway.details, 'giveaway-detail__text');
      if (details) body.append(details);

      const steps = String(giveaway.instructions || '').split('\n').map((step) => step.trim()).filter(Boolean);
      if (steps.length && giveaway.status !== 'closed') {
        body.append(element('h4', 'giveaway-detail__subheading', 'How to enter'));
        const list = element('ol', 'giveaway-detail__steps');
        steps.forEach((step) => list.append(element('li', '', step)));
        body.append(list);
      }

      const rulesUrl = giveaway.rulesUrl || campaign.rulesUrl;
      const rulesLabel = campaign.rulesLabel || 'Official rules';
      const entry = element('div', 'giveaway-detail__entry');
      if (giveaway.status === 'closed') {
        entry.append(element('p', 'giveaway-detail__closed', giveaway.closedMessage || 'This giveaway has closed. Thank you to everyone who entered!'));
      } else if (giveaway.status === 'upcoming') {
        entry.append(element('p', 'giveaway-detail__upcoming', giveaway.opensAt ? `Entries open ${formatTime(giveaway.opensAt)}.` : 'Entries open soon.'));
      } else if (giveaway.entryMethod === 'form' && giveaway.fields?.length) {
        entry.append(this.form(giveaway, rulesUrl, rulesLabel));
      } else if (giveaway.entryMethod === 'link' && giveaway.ctaUrl) {
        entry.append(link(giveaway.ctaUrl, 'button giveaway-detail__cta', giveaway.ctaLabel || 'Enter the giveaway'));
      }
      if (giveaway.status === 'open' && giveaway.closesAt) {
        entry.append(element('p', 'giveaway-detail__deadline', `Entries close ${formatTime(giveaway.closesAt)}.`));
      }
      if (entry.childElementCount) body.append(entry);
      if (rulesUrl && !(giveaway.status === 'open' && giveaway.entryMethod === 'form')) {
        const rules = element('p', 'giveaway-detail__rules');
        rules.append(link(rulesUrl, '', rulesLabel));
        body.append(rules);
      }
      panel.append(body);
      return panel;
    }

    form(giveaway, rulesUrl, rulesLabel) {
      const form = element('form', 'giveaway-form');
      form.noValidate = true;
      const prefix = `${this.panelId(giveaway)}-field`;
      form.setAttribute('aria-label', `Enter ${giveaway.title}`);

      giveaway.fields.forEach((field) => form.append(this.field(field, `${prefix}-${field.key}`)));

      if (giveaway.requireRulesAgreement) {
        const wrapper = element('div', 'giveaway-field giveaway-field--checkbox');
        const input = element('input');
        input.type = 'checkbox';
        input.id = `${prefix}-agree`;
        input.name = 'agreeToRules';
        input.required = true;
        const label = element('label');
        label.htmlFor = input.id;
        label.append(document.createTextNode('I agree to the '));
        if (rulesUrl) label.append(link(rulesUrl, '', rulesLabel.toLowerCase() === 'official rules' ? 'official rules' : rulesLabel));
        else label.append(document.createTextNode('official rules'));
        label.append(element('span', 'giveaway-field__required', ' (required)'));
        const error = element('p', 'giveaway-field__error');
        error.id = `${input.id}-error`;
        error.hidden = true;
        wrapper.append(input, label, error);
        form.append(wrapper);
      }

      // Honeypot for bots: hidden from people and assistive technology.
      const trap = element('div', 'giveaway-form__trap');
      trap.setAttribute('aria-hidden', 'true');
      const trapInput = element('input');
      trapInput.type = 'text';
      trapInput.name = 'website';
      trapInput.tabIndex = -1;
      trapInput.autocomplete = 'off';
      trap.append(trapInput);
      form.append(trap);

      const submit = element('button', 'button giveaway-form__submit', this.dataset.submitLabel || 'Submit entry');
      submit.type = 'submit';
      const message = element('p', 'giveaway-form__message');
      message.setAttribute('role', 'status');
      message.tabIndex = -1;
      form.append(submit, message);
      // Without the agreement checkbox, still link the rules beside the form.
      if (rulesUrl && !giveaway.requireRulesAgreement) {
        const rules = element('p', 'giveaway-detail__rules');
        rules.append(link(rulesUrl, '', rulesLabel));
        form.append(rules);
      }
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit(form, giveaway);
      });
      form.addEventListener('input', (event) => this.clearError(event.target));
      form.addEventListener('change', (event) => this.clearError(event.target));
      return form;
    }

    field(field, id) {
      const wrapper = element('div', `giveaway-field giveaway-field--${field.type}`);
      let input;
      if (field.type === 'textarea') {
        input = element('textarea');
        input.rows = 4;
        input.maxLength = 2000;
      } else if (field.type === 'select') {
        input = element('select');
        const placeholder = element('option', '', field.placeholder || 'Choose one');
        placeholder.value = '';
        input.append(placeholder);
        field.options.forEach((option) => {
          const choice = element('option', '', option);
          choice.value = option;
          input.append(choice);
        });
      } else {
        input = element('input');
        input.type = field.type;
        const limits = { email: 254, text: 200, tel: 30 };
        if (limits[field.type]) input.maxLength = limits[field.type];
        if (field.type === 'email') {
          input.autocomplete = 'email';
          input.autocapitalize = 'off';
          input.spellcheck = false;
        }
        if (field.type === 'tel') input.autocomplete = 'tel';
        if (field.key === 'first_name') input.autocomplete = 'given-name';
        if (field.key === 'last_name') input.autocomplete = 'family-name';
      }
      input.id = id;
      input.name = field.key;
      input.required = Boolean(field.required);
      if (field.placeholder && field.type !== 'select' && field.type !== 'checkbox') input.placeholder = field.placeholder;

      const label = element('label', 'giveaway-field__label', field.label);
      label.htmlFor = id;
      if (field.required && field.type !== 'checkbox') label.append(element('span', 'giveaway-field__required', ' (required)'));
      const described = [];
      let help = null;
      if (field.helpText) {
        help = element('p', 'giveaway-field__help', field.helpText);
        help.id = `${id}-help`;
        described.push(help.id);
      }
      const error = element('p', 'giveaway-field__error');
      error.id = `${id}-error`;
      error.hidden = true;
      if (described.length) input.setAttribute('aria-describedby', described.join(' '));

      if (field.type === 'checkbox') {
        if (field.required) label.append(element('span', 'giveaway-field__required', ' (required)'));
        wrapper.append(input, label);
      } else {
        wrapper.append(label, input);
      }
      if (help) wrapper.append(help);
      wrapper.append(error);
      return wrapper;
    }

    setError(input, text) {
      const error = this.querySelector(`#${CSS.escape(input.id)}-error`);
      input.setAttribute('aria-invalid', 'true');
      const described = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== error?.id);
      if (error) {
        error.textContent = text;
        error.hidden = false;
        described.push(error.id);
      }
      input.setAttribute('aria-describedby', described.join(' '));
    }

    clearError(input) {
      if (!(input instanceof HTMLElement) || input.getAttribute('aria-invalid') !== 'true') return;
      input.removeAttribute('aria-invalid');
      const error = this.querySelector(`#${CSS.escape(input.id)}-error`);
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      const described = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== error?.id);
      if (described.length) input.setAttribute('aria-describedby', described.join(' '));
      else input.removeAttribute('aria-describedby');
    }

    validationText(input) {
      if (input.validity.valueMissing) {
        return input.type === 'checkbox' ? 'Please check this box to continue.' : 'This field is required.';
      }
      if (input.validity.typeMismatch && input.type === 'email') return 'Enter a valid email address.';
      return input.validationMessage || 'Check this field.';
    }

    async submit(form, giveaway) {
      const submit = form.querySelector('.giveaway-form__submit');
      const message = form.querySelector('.giveaway-form__message');
      if (submit.getAttribute('aria-busy') === 'true') return;
      message.textContent = '';
      message.classList.remove('is-error');

      const controls = [...form.querySelectorAll('input:not([name="website"]), select, textarea')];
      controls.forEach((input) => this.clearError(input));
      const invalid = controls.filter((input) => !input.checkValidity());
      if (invalid.length) {
        invalid.forEach((input) => this.setError(input, this.validationText(input)));
        invalid[0].focus();
        return;
      }

      const fields = {};
      giveaway.fields.forEach((field) => {
        const input = form.elements.namedItem(field.key);
        if (!input) return;
        fields[field.key] = field.type === 'checkbox' ? input.checked : input.value;
      });
      const body = {
        campaign: this.campaign,
        giveawayId: giveaway.id,
        fields,
        agreeToRules: Boolean(form.elements.namedItem('agreeToRules')?.checked),
        website: form.elements.namedItem('website')?.value || ''
      };

      submit.setAttribute('aria-busy', 'true');
      submit.disabled = true;
      try {
        const response = await fetch(`${this.endpoint}/giveaways/entries`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const result = await response.json().catch(() => ({}));
        if (response.ok && result.ok) {
          this.success(form, result.duplicate ? result.message : (result.message || giveaway.successMessage || "You're entered! Good luck."));
          return;
        }
        if (response.status === 400 && result.fieldErrors) {
          const errors = Object.entries(result.fieldErrors);
          errors.forEach(([key, text]) => {
            const input = form.elements.namedItem(key);
            if (input instanceof HTMLElement) this.setError(input, text);
          });
          const first = form.elements.namedItem(errors[0]?.[0]);
          if (first instanceof HTMLElement) first.focus();
          this.fail(message, result.error || 'Please check the highlighted fields.', false);
          return;
        }
        if (response.status === 409) {
          this.fail(message, result.error || 'This giveaway is not taking entries right now.');
          this.load();
          return;
        }
        this.fail(message, result.error || 'Something went wrong. Please try again.');
      } catch {
        this.fail(message, 'We could not send your entry. Check your connection and try again.');
      } finally {
        submit.removeAttribute('aria-busy');
        submit.disabled = false;
      }
    }

    fail(message, text, focus = true) {
      message.textContent = text;
      message.classList.add('is-error');
      if (focus) message.focus();
    }

    success(form, text) {
      const success = element('div', 'giveaway-form__success');
      success.setAttribute('role', 'status');
      success.tabIndex = -1;
      success.append(element('p', 'giveaway-form__success-title', 'Thank you!'));
      success.append(element('p', '', text));
      form.replaceWith(success);
      success.focus();
    }
  }

  customElements.define('giveaway-weeks', GiveawayWeeks);
})();
